import {useCallback, useEffect, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import {Hub} from "aws-amplify/utils";
import {getCurrentUser, signInWithRedirect} from "aws-amplify/auth";
import {useTranslation} from "react-i18next";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import LoginIcon from "@mui/icons-material/Login";
import {IDENTITY_CENTER_PROVIDER_NAME} from "../config/identityCenter";
import {AuthCard, ErrorDetails} from "../components/ui/AuthCard";
import {colors} from "../theme/tokens";

type SessionCheck = "checking" | "signed-out";

export function LoginPage() {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [sessionCheck, setSessionCheck] = useState<SessionCheck>("checking");
    const [identityProviderError, setIdentityProviderError] = useState(() => readIdentityProviderError(searchParams));

    useEffect(() => {
        document.title = t("LOGIN_PAGE_TITLE");
    }, [t]);

    // An operator who still holds a session should land in the app without seeing the sign-in prompt
    // flash past, so nothing renders until this resolves.
    useEffect(() => {
        let active = true;
        getCurrentUser()
                .then(() => {
                    if (active) navigate("/", {replace: true});
                })
                .catch(() => {
                    if (active) setSessionCheck("signed-out");
                });
        return () => {
            active = false;
        };
    }, [navigate]);

    // The redirect back from Identity Center resolves the session asynchronously; Hub is what tells us
    // it finished.
    useEffect(() => {
        return Hub.listen("auth", ({payload}) => {
            if (payload.event === "signInWithRedirect") {
                navigate("/", {replace: true});
            }
            if (payload.event === "signInWithRedirect_failure") {
                setSessionCheck("signed-out");
            }
        });
    }, [navigate]);

    const signIn = useCallback(() => {
        void signInWithRedirect({provider: {custom: IDENTITY_CENTER_PROVIDER_NAME}});
    }, []);

    return (
            <AuthCard title={t("LOGIN_HEADING")} explanation={t("LOGIN_EXPLANATION")} data-testid="login-page">
                {identityProviderError && (
                        <ErrorDetails
                                title={t("LOGIN_IDP_ERROR_TITLE")}
                                details={identityProviderError}
                                close={{label: t("CLOSE"), onClose: () => setIdentityProviderError(null)}}
                                data-testid="login-idp-error"
                        />
                )}

                {sessionCheck === "checking" ? (
                        <Box sx={{display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5, py: 1}}>
                            <CircularProgress size={18} />
                            <Typography variant="body2" sx={{color: colors.muted}}>{t("LOGIN_CHECKING_SESSION")}</Typography>
                        </Box>
                ) : (
                        <>
                            <Button variant="contained" size="large" startIcon={<LoginIcon />} onClick={signIn} fullWidth>
                                {t("LOGIN_WITH_IDENTITY_CENTER")}
                            </Button>
                            {identityProviderError && (
                                    <Typography variant="caption" sx={{color: colors.faint, textAlign: "center"}}>
                                        {t("LOGIN_COPY_ERROR_HINT")}
                                    </Typography>
                            )}
                        </>
                )}
            </AuthCard>
    );
}

/**
 * Identity Center reports a refused sign-in by redirecting back with OAuth error parameters. Without
 * this the operator sees an empty login screen and no indication of what went wrong - and this page has
 * no other diagnostics.
 */
function readIdentityProviderError(searchParams: URLSearchParams): string | null {
    const error = searchParams.get("error");
    const description = searchParams.get("error_description");
    if (!error && !description) return null;
    return [error, description].filter(Boolean).join(": ");
}
